/* istanbul ignore file */
import { FileExtension } from '../../models/file-type';
import { AfterViewInit, OnInit, Component, ElementRef, ViewChild, OnDestroy } from '@angular/core';
import { AlertController, LoadingController, ModalController, NavParams, Platform } from '@ionic/angular';
import * as RecordRTC from 'recordrtc';
import { Question, PhotoOrientation, PhotoOverlay, QuestionTypes } from '../../models/question.model';
import { PhotoVideoService, CameraView } from 'src/app/services/photo-video/photo-video.service';
import { File } from 'src/app/services/questionnaire/questionnaire.service';
import { blobToFile } from 'src/app/utilities/file';
import { MediaStreamService } from '../../services/media-stream/media-stream.service';
import { TranslateService } from '@ngx-translate/core';
import { TimerService, VideoStatus } from '../../services/timer/timer.service';
import { Subscription } from 'rxjs';
import { isPlatform } from '@ionic/core';
import { ObjectUrlService } from 'src/app/services/object-url/object-url.service';
import { hasFrontBackCamera } from 'src/app/utilities/camera';

export const MAX_VIDEO_LENGTH_SECONDS = 180;

@Component({
  selector: 'app-html5-canvas',
  templateUrl: './html5-canvas.page.html',
  styleUrls: ['./html5-canvas.page.scss'],
})
export class Html5CanvasPage implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('video') video: ElementRef;
  @ViewChild('canvas') canvas: ElementRef<HTMLCanvasElement>;

  QuestionTypes = QuestionTypes;
  PhotoOrientation = PhotoOrientation;
  PhotoOverlay = PhotoOverlay;
  CameraView = CameraView;

  public error;
  public shouldFaceUser = false;
  public recorder;
  public showStopRecording: boolean;
  public question: Question = new Question();
  public timeInSeconds: number;
  public captured = false;
  public canvasElement: HTMLCanvasElement;
  public videoElement: HTMLVideoElement;
  public timerSubscription: Subscription;
  public disableFlip = false;
  public hideFlip = false;
  public recordingFinished = false;
  public resumeListener: Subscription = new Subscription();

  constructor(
    private mediaStream: MediaStreamService,
    public modalCtrl: ModalController,
    public alertController: AlertController,
    public params: NavParams,
    private photoVideoService: PhotoVideoService,
    private translateService: TranslateService,
    public timerService: TimerService,
    public platform: Platform,
    public loadingController: LoadingController,
    public translate: TranslateService,
    private objectURLsvc: ObjectUrlService
  ) {
    this.question = this.params.get('question');
    this.shouldFaceUser = this.question.camera === CameraView.FrontView;

    this.timeInSeconds = (this.question.restrictVideoLength && this.question.maxVideoTime) || MAX_VIDEO_LENGTH_SECONDS;
  }

  ngOnInit() {
    if (this.platform.is('cordova') && this.question.orientation && this.question.orientation !== PhotoOrientation.notFixed) {
      screen.orientation.lock(this.question.orientation as OrientationLockType);
    }
    this.timerService.initTimer(this.timeInSeconds, MAX_VIDEO_LENGTH_SECONDS);
    this.timerSubscription = this.timerService.stream$.subscribe(async videoSttus => {
      if (videoSttus === VideoStatus.maximum) {
        this.saveVideoRecording();
      } else if (videoSttus === VideoStatus.finished) {
        this.recordingFinished = true;
      }
    });
    this.initCamera();
  }

  ionViewWillEnter() {
    this.resumeListener = this.platform.resume.subscribe(async () => {
      if (this.mediaStream.stream) {
        this.loadingController.dismiss();
        await this.mediaStream.stopStreaming();
        this.videoElement.srcObject = null;
        this.initCamera();
      }
    });

    this.setCameraOverlayStyle();
  }

  setCameraOverlayStyle() {
    if (this.question.overlay === PhotoOverlay.noOverlay) {
      return;
    }
    const maxIteration = 30; // max 3s waiting the DOM
    let i = 0;
    const intv = setInterval(() => {
      i++;
      const overlay = document.querySelector('.camera__overlay') as HTMLElement;
      if (overlay || i > maxIteration) {
        clearInterval(intv);
        overlay.style.width = '100%';
        overlay.style.height = '100%';
      }
    }, 100);
  }

  ionViewWillLeave() {
    this.resumeListener.unsubscribe();
    this.timerSubscription.unsubscribe();
  }

  ngOnDestroy() {
    if (this.platform.is('cordova')) {
      screen.orientation.unlock();
    }
  }

  async ngAfterViewInit() {
    this.videoElement = this.video.nativeElement as HTMLVideoElement;
    this.canvasElement = this.canvas.nativeElement as HTMLCanvasElement;
    if (this.shouldFaceUser) {
      this.videoElement.style.transform = 'scaleX(-1)';
    }
  }

  async initCamera() {
    try {
      await this.handleInitCamera();
    } catch (error) {
      this.error = error;
      this.modalCtrl.dismiss({ error });
      switch (true) {
        case error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError':
          // required track is missing
          this.error = 'Devices not found';
          break;
        case error.name === 'NotReadableError' || error.name === 'TrackStartError':
          // webcam or mic are already in use
          this.error = 'Devices not readable or already in use';
          break;
        case error.name === 'OverconstrainedError' || error.name === 'ConstraintNotSatisfiedError':
          // constraints can not be satisfied by avb. devices
          this.error = 'Constraints not accepted';
          break;
        case error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError':
          // permission denied in browser
          this.error = 'Permission denied';
          break;
        case error.name === 'TypeError' || error.name === 'TypeError':
          // empty constraints object
          this.error = 'Constraints error';
          break;
      }
    } finally {
      this.loadingController.dismiss();
    }
  }

  async handleInitCamera() {
    const loading = await this.loadingController.create({
      spinner: 'circles',
      cssClass: 'custom-camera-loading',
      translucent: true,
    });
    await loading.present();
    if (!this.platform.is('cordova')) {
      this.hideFlip = !(await hasFrontBackCamera());
    }

    const stream = await navigator.mediaDevices.getUserMedia({
      video: {
        facingMode: this.shouldFaceUser ? 'user' : 'environment',
      },
    });
    this.mediaStream.setStream(stream);
    this.videoElement.srcObject = this.mediaStream.stream;

    loading.dismiss();
  }

  async startRecording() {
    this.recordingFinished = false;
    this.showStopRecording = true;
    this.recorder = new RecordRTC(this.videoElement.srcObject, {
      type: 'video',
      mimeType: FileExtension.webm,
    });
    this.recorder.startRecording();
    this.timerService.startTimer();
    this.captured = true;
  }

  async stopRecording() {
    if (this.timerService.passedTime < this.timerService.videoLimit && this.question.restrictVideoLength) {
      this.timerService.pauseTimer();
      this.recorder.pauseRecording();
      await this.presentAlertConfirm(
        this.translateService.instant('Html5CanvasPage.recordingNotCompleted'),
        this.translateService.instant('Html5CanvasPage.recordingNotCompletedText'),
        this.translateService.instant('CommonTexts.yes'),
        this.translateService.instant('CommonTexts.no'),
        'recording'
      );
      return;
    }
    this.saveVideoRecording();
  }

  async capture() {
    if (!this.captured) {
      this.captured = true;
      this.getSnap();
      const blob = await new Promise<Blob>(resolve => this.canvasElement.toBlob(resolve, 'image/jpeg'));
      this.question.videoPhotoResult = await this.photoVideoService.convertBlobToFile('image.jpg', 'image/jpeg', blob);
      this.question.manualVideoPhotoUpload = false;
      this.photoVideoService.videoPhotoResultChanged.next(this.question);
      this.videoElement.srcObject = null;
      this.mediaStream.stopStreaming();
      this.photoVideoService.isMediaUploaded = true;
      this.photoVideoService.setMediaObject(this.question);
      this.modalCtrl.dismiss();
    }
  }

  cancel() {
    if (this.question.type === QuestionTypes.VideoQuestion) {
      this.timerService.pauseTimer();
      this.presentAlertConfirm(
        this.translateService.instant('Html5CanvasPage.cancelVideoHeader'),
        this.translateService.instant('Html5CanvasPage.cancelVideoText'),
        this.translateService.instant('CommonTexts.yes'),
        this.translateService.instant('CommonTexts.no'),
        'cancel'
      );
    } else {
      if (this.captured) {
        this.presentAlertConfirm(
          this.translateService.instant('Html5CanvasPage.cancelCaptureHeader'),
          this.translateService.instant('Html5CanvasPage.cancelCaptureText'),
          this.translateService.instant('CommonTexts.yes'),
          this.translateService.instant('CommonTexts.no'),
          'cancel'
        );
        return;
      }
      this.mediaStream.stopStreaming();
      this.modalCtrl.dismiss();
    }
  }

  async presentAlertConfirm(header, message, cancelText, yesText, type) {
    const alert = await this.alertController.create({
      header,
      message,
      buttons: [
        {
          text: cancelText,
          role: 'cancel',
          cssClass: 'secondary',
          handler: () => {
            this.alertCancelHandler(type);
          },
        },
        {
          text: yesText,
          handler: async () => {
            if (type === 'recording') {
              this.timerService.resumeTimer();
              this.recorder.resumeRecording();
            } else if (type === 'cancel') {
            } else if (type === 'redo-recording') {
              this.showStopRecording = false;
              this.captured = false;
              this.recorder = null;
              this.timerService.initTimer(this.timeInSeconds, MAX_VIDEO_LENGTH_SECONDS);
            }
          },
        },
      ],
    });
    await alert.present();
  }

  alertOkHandler(type) {
    switch (type) {
      case 'recording':
        this.timerService.resumeTimer();
        this.recorder.resumeRecording();
        break;
      case 'cancel':
        break;
      case 'redo-recording':
        this.showStopRecording = false;
        this.recorder = null;
        this.timerService.initTimer(this.timeInSeconds, MAX_VIDEO_LENGTH_SECONDS);
        break;
    }
  }

  alertCancelHandler(type) {
    switch (type) {
      case 'recording':
        this.presentAlertConfirm(
          this.translateService.instant('Html5CanvasPage.shortRecordingHeader'),
          this.translateService.instant('Html5CanvasPage.shortRecordingText'),
          this.translateService.instant('Html5CanvasPage.shortRecordingCancel'),
          this.translateService.instant('Html5CanvasPage.shortRecordingRetry'),
          'redo-recording'
        );
        break;
      case 'cancel':
        this.mediaStream.stopStreaming();
        if (this.captured) {
          this.canvasElement.getContext('2d').clearRect(0, 0, this.canvasElement.width, this.canvasElement.height);
          this.captured = false;
          this.initCamera();
        } else {
          this.modalCtrl.dismiss();
        }
        break;
      case 'redo-recording':
        this.modalCtrl.dismiss();
        break;
    }
  }

  async flip() {
    this.disableFlip = true;
    this.mediaStream.stopStreaming();
    this.shouldFaceUser = !this.shouldFaceUser;
    if (this.shouldFaceUser) {
      this.videoElement.style.transform = 'scaleX(-1)';
    } else {
      this.videoElement.style.removeProperty('transform');
    }
    await this.initCamera();
    this.disableFlip = false;
  }

  async saveVideoRecording() {
    this.showStopRecording = false;
    const loading = await this.loadingController.create({
      message: `<strong>${this.translateService.instant('Html5CanvasPage.processingVideo')}</strong><br>
      <em>${this.translateService.instant('Html5CanvasPage.thisMayTakeaMinute')}</em>`,
    });
    await loading.present();
    const blob = await new Promise<Blob>(resolve => {
      this.recorder.stopRecording(() => {
        this.getSnap();
        resolve(this.recorder.getBlob());
      });
    });
    const imageBlob = await new Promise<Blob>(resolve => this.canvasElement.toBlob(resolve, 'image/jpeg'));
    const file = await this.photoVideoService.convertBlobToFile('video.webm', FileExtension.webm, blob, imageBlob);

    this.videoElement.srcObject = null;
    this.recorder.reset();
    this.mediaStream.stopStreaming();
    this.question.videoPhotoResult = file;
    this.question.manualVideoPhotoUpload = false;
    this.photoVideoService.videoPhotoResultChanged.next(this.question);
    this.photoVideoService.isMediaUploaded = true;
    this.photoVideoService.setMediaObject(this.question);
    await loading.dismiss();
    await this.modalCtrl.dismiss();
  }

  getSnap() {
    this.canvasElement.width = this.videoElement.videoWidth;
    this.canvasElement.height = this.videoElement.videoHeight;
    const canvasContext = this.canvasElement.getContext('2d');
    if (this.shouldFaceUser) {
      canvasContext.translate(this.canvasElement.width, 0);
      canvasContext.scale(-1, 1);
    }
    canvasContext.drawImage(this.videoElement, 0, 0);
  }
}
